import { useState, useEffect, useRef } from 'react'
import {
  MessageSquare,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Ban,
  Clock,
  Flag,
  Shield,
  QrCode,
  Unplug,
} from 'lucide-react'
import {
  useWhatsAppMailboxes,
  useDeleteWhatsAppMailbox,
  useResetDailyMessageCount,
} from '@hooks/useWhatsAppMailboxes'
import { useUIStore } from '@stores/ui'
import { WhatsAppMailbox } from '@/types'
import { formatDateTime } from '@lib/utils'
import { WhatsAppMailboxFilters, whatsappMailboxService } from '@services/whatsapp-mailbox.service'
import { WhatsAppQRCodeModal } from './WhatsAppQRCodeModal'

interface WhatsAppMailboxListProps {
  onEdit: (mailbox: WhatsAppMailbox) => void
}

export function WhatsAppMailboxList({ onEdit }: WhatsAppMailboxListProps) {
  const [filters, setFilters] = useState<WhatsAppMailboxFilters>({
    page: 1,
    pageSize: 10,
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedMailbox, setSelectedMailbox] = useState<WhatsAppMailbox | null>(null)

  const { data, isLoading, error } = useWhatsAppMailboxes(filters)
  const deleteMutation = useDeleteWhatsAppMailbox()
  const resetCountMutation = useResetDailyMessageCount()
  const showToast = useUIStore((state) => state.showToast)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters({ ...filters, search: searchTerm, page: 1 })
  }

  const handleStatusFilter = (status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'RESTRICTED' | undefined) => {
    setFilters({ ...filters, status, page: 1 })
  }

  const handleConnectionTypeFilter = (connectionType: 'BUSINESS_API' | 'BAILEYS' | undefined) => {
    setFilters({ ...filters, connectionType, page: 1 })
  }

  const handleQualityFilter = (qualityRating: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN' | undefined) => {
    setFilters({ ...filters, qualityRating, page: 1 })
  }

  const handleAccountStatusFilter = (
    accountStatus:
      | 'CONNECTED'
      | 'DISCONNECTED'
      | 'FLAGGED'
      | 'RATE_LIMITED'
      | 'PENDING'
      | 'BANNED'
      | undefined
  ) => {
    setFilters({ ...filters, accountStatus, page: 1 })
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this WhatsApp mailbox?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleResetCount = async (id: number) => {
    if (confirm('Reset daily message count for this mailbox?')) {
      await resetCountMutation.mutateAsync(id)
    }
  }

  const handleConnect = (mailbox: WhatsAppMailbox) => {
    setSelectedMailbox(mailbox)
    setShowQRModal(true)
  }

  const handleDisconnect = async (id: number) => {
    if (confirm('Are you sure you want to disconnect this WhatsApp mailbox?')) {
      try {
        await whatsappMailboxService.disconnectMailbox(id)
        // Refresh the list to show updated connection status
        window.location.reload()
      } catch (error) {
        console.error('Failed to disconnect mailbox:', error)
        showToast('Failed to disconnect mailbox. Please try again.', 'error')
      }
    }
  }

  const handleQRConnected = () => {
    // Refresh the list to show updated connection status
    window.location.reload()
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load WhatsApp mailboxes</p>
      </div>
    )
  }

  const mailboxes = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or phone number..."
            className="input-modern pl-10"
          />
        </div>
        <button
          type="submit"
          className="btn-primary"
        >
          Search
        </button>
      </form>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        {/* Status Filter */}
        <div className="flex gap-2">
          <span className="self-center text-sm font-medium text-slate-400">Status:</span>
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === undefined
                ? 'bg-cyan-500 text-slate-900'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleStatusFilter('ACTIVE')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'ACTIVE'
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => handleStatusFilter('INACTIVE')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'INACTIVE'
                ? 'bg-slate-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Inactive
          </button>
          <button
            onClick={() => handleStatusFilter('SUSPENDED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'SUSPENDED'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Suspended
          </button>
          <button
            onClick={() => handleStatusFilter('RESTRICTED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'RESTRICTED'
                ? 'bg-rose-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Restricted
          </button>
        </div>

        {/* Connection Type Filter */}
        <div className="flex gap-2">
          <span className="self-center text-sm font-medium text-gray-700">Connection:</span>
          <button
            onClick={() => handleConnectionTypeFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.connectionType === undefined
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleConnectionTypeFilter('BAILEYS')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.connectionType === 'BAILEYS'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            QR Code
          </button>
          <button
            onClick={() => handleConnectionTypeFilter('BUSINESS_API')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.connectionType === 'BUSINESS_API'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Business API
          </button>
        </div>

        {/* Quality Rating Filter */}
        <div className="flex gap-2">
          <span className="self-center text-sm font-medium text-gray-700">Quality:</span>
          <button
            onClick={() => handleQualityFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.qualityRating === undefined
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleQualityFilter('GREEN')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.qualityRating === 'GREEN'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Green
          </button>
          <button
            onClick={() => handleQualityFilter('YELLOW')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.qualityRating === 'YELLOW'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Yellow
          </button>
          <button
            onClick={() => handleQualityFilter('RED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.qualityRating === 'RED'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Red
          </button>
        </div>

        {/* Account Status Filter */}
        <div className="flex gap-2">
          <span className="self-center text-sm font-medium text-gray-700">Account:</span>
          <button
            onClick={() => handleAccountStatusFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.accountStatus === undefined
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleAccountStatusFilter('CONNECTED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.accountStatus === 'CONNECTED'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Connected
          </button>
          <button
            onClick={() => handleAccountStatusFilter('FLAGGED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.accountStatus === 'FLAGGED'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Flagged
          </button>
        </div>
      </div>

      {/* Mailbox List */}
      {mailboxes.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
          <MessageSquare className="mb-4 h-16 w-16 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900">No WhatsApp mailboxes found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filters.search || filters.status || filters.connectionType || filters.qualityRating || filters.accountStatus
              ? 'Try adjusting your filters'
              : 'Add your first WhatsApp mailbox to start sending messages'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Mailbox
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Connection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Quality
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Templates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Last Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mailboxes.map((mailbox) => (
                <WhatsAppMailboxRow
                  key={mailbox.id}
                  mailbox={mailbox}
                  onEdit={() => onEdit(mailbox)}
                  onDelete={() => handleDelete(mailbox.id)}
                  onResetCount={() => handleResetCount(mailbox.id)}
                  onConnect={() => handleConnect(mailbox)}
                  onDisconnect={() => handleDisconnect(mailbox.id)}
                />
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
              <p className="text-sm text-gray-700">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedMailbox && (
        <WhatsAppQRCodeModal
          mailboxId={selectedMailbox.id}
          mailboxName={selectedMailbox.name}
          onClose={() => {
            setShowQRModal(false)
            setSelectedMailbox(null)
          }}
          onConnected={handleQRConnected}
        />
      )}
    </div>
  )
}

interface WhatsAppMailboxRowProps {
  mailbox: WhatsAppMailbox
  onEdit: () => void
  onDelete: () => void
  onResetCount: () => void
  onConnect: () => void
  onDisconnect: () => void
}

function WhatsAppMailboxRow({ mailbox, onEdit, onDelete, onResetCount, onConnect, onDisconnect }: WhatsAppMailboxRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  // Connection type badge
  const getConnectionTypeBadge = () => {
    const isConnected = mailbox.isConnected
    switch (mailbox.connectionType) {
      case 'BAILEYS':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
            isConnected
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-600'
          }`}>
            <MessageSquare size={14} />
            QR Code {isConnected ? '✓' : '○'}
          </span>
        )
      case 'BUSINESS_API':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
            isConnected
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-600'
          }`}>
            <MessageSquare size={14} />
            Business API {isConnected ? '✓' : '○'}
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            <MessageSquare size={14} />
            Unknown
          </span>
        )
    }
  }

  // Status badge
  const getStatusBadge = () => {
    switch (mailbox.status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
            <CheckCircle size={14} />
            Active
          </span>
        )
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            <XCircle size={14} />
            Inactive
          </span>
        )
      case 'SUSPENDED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
            <AlertTriangle size={14} />
            Suspended
          </span>
        )
      case 'RESTRICTED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
            <Shield size={14} />
            Restricted
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            Unknown
          </span>
        )
    }
  }

  // Quality rating badge
  const getQualityBadge = () => {
    switch (mailbox.qualityRating) {
      case 'GREEN':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
            <CheckCircle size={14} />
            High Quality
          </span>
        )
      case 'YELLOW':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
            <AlertTriangle size={14} />
            Medium Quality
          </span>
        )
      case 'RED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
            <AlertCircle size={14} />
            Low Quality
          </span>
        )
      case 'UNKNOWN':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            <AlertCircle size={14} />
            Unknown
          </span>
        )
    }
  }

  // Account status badge
  const getAccountStatusBadge = () => {
    switch (mailbox.accountStatus) {
      case 'CONNECTED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
            <CheckCircle size={14} />
            Connected
          </span>
        )
      case 'DISCONNECTED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            <XCircle size={14} />
            Disconnected
          </span>
        )
      case 'FLAGGED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
            <Flag size={14} />
            Flagged
          </span>
        )
      case 'RATE_LIMITED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800">
            <Clock size={14} />
            Rate Limited
          </span>
        )
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
            <Clock size={14} />
            Pending
          </span>
        )
      case 'BANNED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
            <Ban size={14} />
            Banned
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            Unknown
          </span>
        )
    }
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="flex items-center">
          <MessageSquare className="mr-3 h-5 w-5 text-gray-400" />
          <div>
            <div className="font-medium text-gray-900">{mailbox.name}</div>
            <div className="text-sm text-gray-500">{mailbox.phoneNumber}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">{getConnectionTypeBadge()}</td>
      <td className="px-6 py-4">{getStatusBadge()}</td>
      <td className="px-6 py-4">{getQualityBadge()}</td>
      <td className="px-6 py-4">{getAccountStatusBadge()}</td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-700">
          {mailbox.approvedTemplates} approved
          {mailbox.pendingTemplates > 0 && (
            <span className="ml-1 text-xs text-gray-500">
              ({mailbox.pendingTemplates} pending)
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500">
        {mailbox.lastUsedAt ? formatDateTime(mailbox.lastUsedAt) : 'Never'}
      </td>
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="relative inline-block" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/20 overflow-hidden">
              <button
                onClick={() => {
                  onEdit()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Edit size={16} className="text-cyan-400" /> Edit Mailbox
              </button>

              {/* Connect/Disconnect buttons - only for BAILEYS connection type */}
              {mailbox.connectionType === 'BAILEYS' && !mailbox.isConnected && (
                <button
                  onClick={() => {
                    onConnect()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                >
                  <QrCode size={16} /> Connect WhatsApp
                </button>
              )}
              {mailbox.connectionType === 'BAILEYS' && mailbox.isConnected && (
                <button
                  onClick={() => {
                    onDisconnect()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-amber-400 hover:bg-amber-500/10 transition-colors"
                >
                  <Unplug size={16} /> Disconnect
                </button>
              )}

              <button
                onClick={() => {
                  onResetCount()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <RefreshCw size={16} className="text-amber-400" /> Reset Count
              </button>
              <div className="border-t border-slate-700">
                <button
                  onClick={() => {
                    onDelete()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-6">
          <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-1/4 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  )
}
